import React from "react";
import { Typography, Divider, Tag } from "antd";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useContactModal } from "../context/ContactModalContext";
import {
  Brain,
  Rocket,
  Shield,
  Users,
  Lightbulb,
  Code,
  Target,
  Globe,
  PhoneCallIcon,
} from "lucide-react";
import { useTranslation } from "../i18n/useTranslation";

const { Title, Text, Paragraph } = Typography;

const About = () => {
  const { t } = useTranslation();
  const { openContactModal } = useContactModal();

  // 团队成员数据
  const teamMembers = [
    {
      name: t("aboutTeamMember1Name"),
      role: t("aboutTeamMember1Role"),
      description: t("aboutTeamMember1Desc"),
    },
    {
      name: t("aboutTeamMember2Name"),
      role: t("aboutTeamMember2Role"),
      description: t("aboutTeamMember2Desc"),
    },
    {
      name: t("aboutTeamMember3Name"),
      role: t("aboutTeamMember3Role"),
      description: t("aboutTeamMember3Desc"),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <Title level={1} className="mb-4">
                {t("aboutHeroTitle")}
              </Title>
              <div className="text-gray-600 py-6 text-base">
                {t("aboutHeroDesc")}
              </div>
              {/* <div className="flex gap-4 mt-2 mb-6">
                <Button variant="blue-griadient" size="lg">
                  了解更多
                </Button>
                <Button variant="secondary" size="lg">
                  联系我们
                </Button>
              </div> */}
            </div>
            <div className="md:w-1/2 max-w-[560px]">
              <img src={require("../assets/img/about_hero.webp")} />
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100 uppercase rounded-full shadow-sm">
              {t("aboutCompanyTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("aboutCompanyTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("aboutCompanyDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center text-white bg-blue-600 rounded-lg flex-shrink-0">
                    <Brain className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {t("aboutMissionTitle")}
                    </h3>
                    <p className="text-slate-600">
                      {t("aboutMissionDesc")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center text-white bg-blue-600 rounded-lg flex-shrink-0">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {t("aboutVisionTitle")}
                    </h3>
                    <p className="text-slate-600">
                      {t("aboutVisionDesc")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center text-white bg-blue-600 rounded-lg flex-shrink-0">
                    <Target className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {t("aboutGoalTitle")}
                    </h3>
                    <p className="text-slate-600">
                      {t("aboutGoalDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src={require("../assets/img/about-team.webp")} />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-gray-100 py-24">
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100 uppercase rounded-full shadow-sm">
              {t("aboutValuesTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("aboutValuesTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Lightbulb className="w-8 h-8" />,
                title: t("aboutValue1Title"),
                description: t("aboutValue1Desc"),
              },
              {
                icon: <Code className="w-8 h-8" />,
                title: t("aboutValue2Title"),
                description: t("aboutValue2Desc"),
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: t("aboutValue3Title"),
                description: t("aboutValue3Desc"),
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: t("aboutValue4Title"),
                description: t("aboutValue4Desc"),
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="inline-flex h-12 w-12 mb-4 items-center justify-center text-white bg-blue-600 rounded-lg">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section
        className="py-24 md:pb-32 bg-white bg-center"
        style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
      >
        <div className="container">
          <div className="md:max-w-5xl mb-12 mx-auto text-center">
            <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100 uppercase rounded-full shadow-sm">
              {t("aboutTeamTag")}
            </span>
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight font-bold tracking-tight text-slate-800">
              {t("aboutTeamTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-500">
              {t("aboutTeamDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 p-6"
              >
                <h3 className="text-xl font-bold text-slate-800 mb-1">
                  {member.name}
                </h3>
                <p className="text-blue-600 mb-3">{member.role}</p>
                <p className="text-slate-600 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us - same style as Solutions CTA */}
      <section className="pt-12 pb-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col items-center justify-center pt-14 md:pt-16 pb-8 md:pb-12 px-8 md:px-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t("aboutJoinUsTitle")}
              </h2>
              <p className="text-base md:text-lg text-blue-100 mb-6 max-w-2xl">
                {t("aboutJoinUsDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={openContactModal}
                >
                  <PhoneCallIcon />
                  {t("aboutJoinUsButton")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
